import { useCallback, useEffect, useRef } from 'react';
import { ABOUT_TEXT, CONTACT, EXPERIENCES, STACK } from '../constants';

// Cards ride an arc whose centre sits off to the right of the sidebar,
// so the selected card bulges out to the left and the rest fan away.
const CARD_W = 150;
const CARD_H = 94;
const RADIUS = 600;
const STEP = 0.17;
const SPINE_GAP = 18; // radians between neighbouring cards
const FOCUS_SCALE = 1.44;
const VISIBLE = 4;
const DRAG_PX = 96; // pointer travel per card

const initials = (name) =>
    name
        .replace(/\(.*\)/, '')
        .split(/[\s./-]+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

function CardArt({ buffer }) {
    switch (buffer.card) {
        case 'image':
            return <img src={buffer.image} alt="" draggable={false} />;
        case 'brand':
            return (
                <div className="art art-brand">
                    <span className="art-mark">ab</span>
                </div>
            );
        case 'text':
            return (
                <div className="art art-text" aria-hidden="true">
                    {ABOUT_TEXT.slice(0, 520)}
                </div>
            );
        case 'timeline': {
            const years = EXPERIENCES.map((e) => parseInt(e.year, 10)).filter(Boolean).sort();
            return (
                <div className="art art-timeline" aria-hidden="true">
                    <span className="art-rail" />
                    {years.map((y, i) => (
                        <span key={y} className="art-node" style={{ '--x': `${12 + (i * 76) / Math.max(1, years.length - 1)}%` }}>
                            <i />
                            {y}
                        </span>
                    ))}
                </div>
            );
        }
        case 'grid':
            return (
                <div className="art art-grid" aria-hidden="true">
                    {Object.values(STACK)
                        .flat()
                        .slice(0, 21)
                        .map((t) => (
                            <span key={t}>{initials(t)}</span>
                        ))}
                </div>
            );
        case 'contact':
            return (
                <div className="art art-contact" aria-hidden="true">
                    <span className="art-at">@</span>
                    <span className="art-mail">{CONTACT.email.toLowerCase()}</span>
                </div>
            );
        default:
            return <div className="art" />;
    }
}

export default function Wheel({ items, index, onSelect }) {
    const hostRef = useRef(null);
    const svgRef = useRef(null);
    const cardRefs = useRef([]);
    const offset = useRef(index);
    const target = useRef(index);
    const indexRef = useRef(index);
    const raf = useRef(0);
    const drag = useRef(null);
    const wheelAcc = useRef(0);
    const wheelAt = useRef(0);

    const layout = useCallback(() => {
        const host = hostRef.current;
        if (!host) return;
        const cy = host.clientHeight / 2;
        const x0 = 4 + (CARD_W * FOCUS_SCALE) / 2; // centre x of the focused card

        // Position on the arc for a (fractional) distance t from the focus.
        const place = (t) => {
            const a = t * STEP;
            const near = Math.min(1, Math.abs(t));
            // Push neighbours clear of the enlarged focus card.
            const gap = Math.sign(t) * near * CARD_H * (FOCUS_SCALE - 1) * 0.75;
            const scale = FOCUS_SCALE - (FOCUS_SCALE - 1) * near;
            return {
                x: x0 + RADIUS * (1 - Math.cos(a)) + near * 30,
                y: cy + RADIUS * Math.sin(a) + gap,
                scale,
                rot: ((-a * 180) / Math.PI) * 0.42,
                edge: (CARD_W * scale) / 2,
            };
        };

        const ticks = [];
        items.forEach((_, i) => {
            const el = cardRefs.current[i];
            if (!el) return;
            const t = i - offset.current;
            const p = place(t);
            const hidden = Math.abs(t) > VISIBLE;
            el.style.transform = `translate(${p.x - CARD_W / 2}px, ${p.y - CARD_H / 2}px) rotate(${p.rot}deg) scale(${p.scale})`;
            el.style.opacity = hidden ? '0' : String(1 - Math.max(0, Math.abs(t) - VISIBLE + 1));
            el.style.zIndex = String(100 - Math.round(Math.abs(t) * 10));
            el.style.setProperty('--shade', String(Math.min(0.72, Math.abs(t) * 0.16)));
            el.style.visibility = hidden ? 'hidden' : 'visible';
            if (!hidden) {
                const sx = p.x - p.edge - SPINE_GAP;
                ticks.push(`M${sx.toFixed(1)} ${p.y.toFixed(1)} L${(p.x - p.edge).toFixed(1)} ${p.y.toFixed(1)}`);
                ticks.push(`M${(sx - 3).toFixed(1)} ${p.y.toFixed(1)} l3 0`);
            }
        });

        // The spine follows the same arc, just left of the cards.
        const svg = svgRef.current;
        if (svg) {
            const pts = [];
            for (let t = -VISIBLE - 1; t <= VISIBLE + 1; t += 0.1) {
                const p = place(t);
                pts.push(`${pts.length ? 'L' : 'M'}${(p.x - p.edge - SPINE_GAP).toFixed(1)} ${p.y.toFixed(1)}`);
            }
            svg.querySelector('.spine').setAttribute('d', pts.join(' '));
            svg.querySelector('.ticks').setAttribute('d', ticks.join(' '));
            const f = place(0);
            const node = svg.querySelector('.node');
            node.setAttribute('cx', (f.x - f.edge - SPINE_GAP).toFixed(1));
            node.setAttribute('cy', f.y.toFixed(1));
        }
    }, [items]);

    const animate = useCallback(() => {
        const diff = target.current - offset.current;
        if (Math.abs(diff) < 0.002) {
            offset.current = target.current;
            raf.current = 0;
            layout();
            return;
        }
        offset.current += diff * 0.16;
        layout();
        raf.current = requestAnimationFrame(animate);
    }, [layout]);

    const kick = useCallback(() => {
        if (!raf.current) raf.current = requestAnimationFrame(animate);
    }, [animate]);

    useEffect(() => {
        indexRef.current = index;
        if (!drag.current) {
            target.current = index;
            kick();
        }
    }, [index, kick]);

    useEffect(() => {
        layout();
        const onResize = () => layout();
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            cancelAnimationFrame(raf.current);
        };
    }, [layout]);

    // Wheel needs a non-passive listener so the page doesn't scroll.
    useEffect(() => {
        const host = hostRef.current;
        const onWheel = (e) => {
            e.preventDefault();
            const now = Date.now();
            if (now - wheelAt.current > 220) wheelAcc.current = 0;
            wheelAt.current = now;
            wheelAcc.current += Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
            if (Math.abs(wheelAcc.current) >= 60) {
                const dir = Math.sign(wheelAcc.current);
                wheelAcc.current = 0;
                const next = Math.min(items.length - 1, Math.max(0, indexRef.current + dir));
                if (next !== indexRef.current) onSelect(next);
            }
        };
        host.addEventListener('wheel', onWheel, { passive: false });
        return () => host.removeEventListener('wheel', onWheel);
    }, [items.length, onSelect]);

    const onPointerDown = (e) => {
        if (e.button !== 0) return;
        drag.current = { y: e.clientY, start: offset.current, moved: false, id: e.pointerId };
    };

    const onPointerMove = (e) => {
        const d = drag.current;
        if (!d) return;
        const dy = e.clientY - d.y;
        if (!d.moved && Math.abs(dy) > 5) {
            d.moved = true;
            hostRef.current.setPointerCapture(d.id);
            hostRef.current.classList.add('is-dragging');
        }
        if (!d.moved) return;
        const max = items.length - 1;
        offset.current = Math.min(max + 0.4, Math.max(-0.4, d.start - dy / DRAG_PX));
        target.current = offset.current;
        cancelAnimationFrame(raf.current);
        raf.current = 0;
        layout();
    };

    const endDrag = () => {
        const d = drag.current;
        drag.current = null;
        if (!d || !d.moved) return;
        hostRef.current.classList.remove('is-dragging');
        const nearest = Math.min(items.length - 1, Math.max(0, Math.round(offset.current)));
        target.current = nearest;
        kick();
        if (nearest !== indexRef.current) onSelect(nearest);
        // Swallow the click that follows a drag.
        hostRef.current.dataset.swallow = '1';
        setTimeout(() => hostRef.current && delete hostRef.current.dataset.swallow, 0);
    };

    return (
        <div
            className="wheel"
            ref={hostRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
        >
            <svg className="wheel-ink" ref={svgRef} aria-hidden="true">
                <path className="spine" />
                <path className="ticks" />
                <circle className="node" r="3.5" />
            </svg>
            <ul className="wheel-list" role="listbox" aria-label="Files">
                {items.map((b, i) => (
                    <li key={b.path} role="presentation">
                        <button
                            type="button"
                            role="option"
                            aria-selected={i === index}
                            aria-label={b.title}
                            className={`wheel-item card-${b.card}`}
                            ref={(el) => (cardRefs.current[i] = el)}
                            tabIndex={i === index ? 0 : -1}
                            onClick={() => {
                                if (hostRef.current?.dataset.swallow) return;
                                onSelect(i);
                            }}
                        >
                            <CardArt buffer={b} />
                            <span className="wheel-cap">{b.label || b.name}</span>
                        </button>
                    </li>
                ))}
            </ul>
            <span className="wheel-mark" aria-hidden="true" style={{ left: `${CARD_W * FOCUS_SCALE + 14}px` }} />
            <p className="wheel-hint">scroll · drag the wheel</p>
        </div>
    );
}
